import { type FC, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { StackColumn } from '@dhl-ui/components'
import { dataLayerPublish } from '@dhl/datalayer'
import { useGetNotificationPreferences } from '@dhl/receiver-profile-api'
import { getDataLayer } from '@dhl/third-party'

import { PresentYellowIllustration } from '@dhl-ui/illustrations'
import { useGetProfile, useGetProfileEmail, useGetProfileLastAddedAddress } from '~/profile/model/profile/hooks.js'
import { useGetInterventionPreferencesByAddressId } from '~/profile/model/preferences/hooks.js'
import { StepDescription } from '~/setup/view/components/StepDescription.js'
import { ProfileSummary } from '~/setup/view/components/ProfileSummary.js'
import { useWizardContext, useSetTitle } from '~/setup/model/context.js'
import { stepWizardId } from '~/setup/model/steps.js'

export const Done: FC = () => {
  const { t } = useTranslation()
  const { registerStepHandler: handleStep, onStepComplete } = useWizardContext()

  useSetTitle(t('portal.profileSetup.done.pageTitle'))
  const { data: profile } = useGetProfile()
  const { data: lastAddedAddress } = useGetProfileLastAddedAddress()
  const { data: primaryEmail } = useGetProfileEmail()
  const { data: notificationPreferences } = useGetNotificationPreferences()

  const emailNotiticationSettings = notificationPreferences?.emailNotificationPreferences?.find(
    (el) => el.emailAddress === primaryEmail
  )

  const addressId = lastAddedAddress?.addressId ?? ''

  const { data: preference } = useGetInterventionPreferencesByAddressId(addressId)

  const [lastAddedEmailAddress] = [...(profile?.emailAddresses ?? [])].filter((e) => !e.isPrimary).reverse()
  const additionalEmailAddress = lastAddedEmailAddress?.email

  const address = (profile?.addresses ?? []).find((a) => a.addressId === addressId)

  const getDataLayerStepData = () => {
    const dataLayerHistory = getDataLayer()?.getHistory() ?? []
    return dataLayerHistory
      .filter((history) => history.meta.id === stepWizardId)
      .reduce((stepData, item) => ({ ...stepData, ...item.meta }), {})
  }

  const setDataLayerData = useCallback(() => {
    let addressDeliveryPreferenceType = 'none'

    if (preference?.servicePoint?.active) {
      addressDeliveryPreferenceType = 'servicepoint'
    }

    if (preference?.agreedPlace?.active) {
      addressDeliveryPreferenceType = 'agreed_place'
    }

    const hasAddress = Boolean(addressId)
    const hasAdditionalEmailAddress = Boolean(additionalEmailAddress)
    const hasMobilePhoneNumber = Boolean(profile?.mobilePhone?.number)
    const hasMailNotifications = emailNotiticationSettings?.isEnabled
    const hasPushNotificationsIncomingParcels =
      notificationPreferences?.pushNotificationPreference.isEnabledForIncomingParcels
    const hasPushNotificationsOutgoingParcels =
      notificationPreferences?.pushNotificationPreference.isEnabledForOutgoingParcels

    dataLayerPublish('IMPRESSION', {
      ...getDataLayerStepData(),
      id: 'setup-wizard-done',
      has_address: hasAddress,
      address_delivery_preference_type: addressDeliveryPreferenceType,
      has_additional_email_address: hasAdditionalEmailAddress,
      has_mobile_phone_number: hasMobilePhoneNumber,
      has_mail_notifications: hasMailNotifications,
      has_push_notifcations_incoming_parcels: hasPushNotificationsIncomingParcels,
      has_push_notifcations_outgoing_parcels: hasPushNotificationsOutgoingParcels,
    })
  }, [
    additionalEmailAddress,
    addressId,
    emailNotiticationSettings?.isEnabled,
    notificationPreferences?.pushNotificationPreference.isEnabledForIncomingParcels,
    notificationPreferences?.pushNotificationPreference.isEnabledForOutgoingParcels,
    preference?.agreedPlace?.active,
    preference?.servicePoint?.active,
    profile?.mobilePhone?.number,
  ])

  useEffect(() => {
    setDataLayerData()
  }, [setDataLayerData])

  handleStep(onStepComplete)

  return (
    <StackColumn gap={3}>
      <StepDescription
        illustration={<PresentYellowIllustration />}
        title={t('portal.profileSetup.done.stepTitle')}
        description={t('portal.profileSetup.done.stepDescription')}
      />

      <ProfileSummary
        additionalEmailAddress={additionalEmailAddress}
        address={address}
        firstName={profile?.firstName}
        lastName={profile?.lastName}
        mobileNumber={profile?.mobilePhone?.number}
        notificationPreferences={{
          emailNotificationsEnabled: emailNotiticationSettings?.isEnabled ?? false,
          pushNotificationsForIncomingParcelsEnabled:
            notificationPreferences?.pushNotificationPreference.isEnabledForIncomingParcels ?? false,
          pushNotificationsForOutgoingParcelsEnabled:
            notificationPreferences?.pushNotificationPreference.isEnabledForOutgoingParcels ?? false,
        }}
        preference={preference}
        primaryEmailAddress={profile?.email}
      />
    </StackColumn>
  )
}
